import { SkillAudience, SkillScope } from "generated-graphql/graphql";

import { type SkillOwner } from "../../SkillAccess/skillAccessTokens";

export type ScoutSkillMenuItem = {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  /** Slash-menu grouping, derived from scope and audience. */
  owner: SkillOwner;
  scope: SkillScope;
  audience: SkillAudience;
};

/** Slugs that more than one of these skills uses, so the menu can say which is which. */
export function sharedSkillSlugs(items: readonly ScoutSkillMenuItem[]): Set<string> {
  const seen = new Set<string>();
  const shared = new Set<string>();
  for (const item of items) {
    if (seen.has(item.slug)) {
      shared.add(item.slug);
    }
    seen.add(item.slug);
  }
  return shared;
}

/** How many recently used skills the menu remembers and shows. */
const MAX_RECENT_SKILLS = 3;

/** Cap on rows shown in the slash menu (recent + rest combined). */
export const MAX_VISIBLE_SKILLS = 5;

function matchesSkillQuery(item: ScoutSkillMenuItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return (
    item.displayName.toLowerCase().includes(q) ||
    item.slug.toLowerCase().includes(q) ||
    item.description.toLowerCase().includes(q)
  );
}

export type SkillMenuSections = {
  /** Recently used skills, most recent first. Empty while a query is typed. */
  recent: ScoutSkillMenuItem[];
  /** Everything else (or every match while a query is typed), by display name. */
  rest: ScoutSkillMenuItem[];
  /** `recent` then `rest`: the order keyboard navigation walks. */
  flat: ScoutSkillMenuItem[];
  /** Slugs several skills share; their rows show where each activates and who sees it. */
  sharedSlugs: Set<string>;
};

export function buildSkillMenuSections({
  items,
  query,
  recentIds,
}: {
  items: ScoutSkillMenuItem[];
  query: string;
  /** Recently picked skill ids, newest first. */
  recentIds: string[];
}): SkillMenuSections {
  const hasQuery = query.trim().length > 0;
  const matched = items.filter((item) => matchesSkillQuery(item, query));
  const byId = new Map(matched.map((item) => [item.id, item]));

  const recent = hasQuery
    ? []
    : recentIds
        .map((id) => byId.get(id))
        .filter((item): item is ScoutSkillMenuItem => Boolean(item))
        .slice(0, MAX_RECENT_SKILLS);
  const recentShown = new Set(recent.map((item) => item.id));
  const restSlots = MAX_VISIBLE_SKILLS - recent.length;
  const rest = matched
    .filter((item) => !recentShown.has(item.id))
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .slice(0, restSlots);

  return { recent, rest, flat: [...recent, ...rest], sharedSlugs: sharedSkillSlugs(items) };
}

/** Move skill `id` to the front of the recent list, deduped and capped. */
export function pushRecentSkill(recentIds: string[], id: string): string[] {
  return [id, ...recentIds.filter((recentId) => recentId !== id)].slice(0, MAX_RECENT_SKILLS);
}

type SlashMenuKeyAction =
  | { type: "highlight"; index: number }
  | { type: "select"; index: number }
  | { type: "close" };

/** What a key does in the open menu; null leaves it to the composer. */
export function slashMenuKeyAction(params: {
  key: string;
  shiftKey: boolean;
  itemCount: number;
  highlightedIndex: number;
}): SlashMenuKeyAction | null {
  const { key, shiftKey, itemCount, highlightedIndex } = params;
  switch (key) {
    case "ArrowDown":
      return { type: "highlight", index: itemCount === 0 ? 0 : (highlightedIndex + 1) % itemCount };
    case "ArrowUp":
      return {
        type: "highlight",
        index: itemCount === 0 ? 0 : (highlightedIndex - 1 + itemCount) % itemCount,
      };
    case "Escape":
      return { type: "close" };
    case "Enter":
    case "Tab":
      // Shift+Enter is a newline; with nothing to pick, Enter sends the message.
      if ((key === "Enter" && shiftKey) || highlightedIndex >= itemCount) {
        return null;
      }
      return { type: "select", index: highlightedIndex };
    default:
      return null;
  }
}
